import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  useCart,
  type BootstrappingUseCartResult,
  type UseCartResult,
  AddToCartModal,
  useCartMutations,
  type UseCartMutations,
  FillingCartModal,
} from '@hungryroot/cart';
import { AccountStatus } from '@hungryroot/customer';
import { isEmbeddedWebview } from '@hungryroot/environment';
import { PendingCartActionsContext, cartActionKey } from '@hungryroot/pho';
import { useCustomerServingSize } from '@hungryroot/plan';
import {
  type ServingSize,
  type OrderDetailV2Get as Order,
} from '@hungryroot/rest';
import {
  DynamicConfigs,
  useDynamicConfiguration,
} from '@hungryroot/rest-hooks';
import { segmentAnalytics as analytics } from '@hungryroot/segment';
import { Modal as StyledSheetOrModal } from '@hungryroot/ui-kit';
import { getShowServingSizeWarningModal } from '../../components/ServingSizeWarningModal/getShowServingSizeWarningModal';
import { ServingSizeWarningModal } from '../../components/ServingSizeWarningModal/ServingSizeWarningModal';
import { useDietaryRestrictionsWarningAck } from '../../hooks/useDietaryRestrictionsWarningAck';
import { getWarningTextForModal } from '../../utils/getWarningTextForModal';
import {
  useDeferredCartActions,
  type RunWhenCartReady,
} from './useDeferredCartActions';

type SharedOrderFields = Pick<
  Order,
  | 'preferred_date'
  | 'cutoff_datetime'
  | 'id'
  | 'status'
  | 'sequence'
  | 'show_flexcredits_message'
  | 'possible_delivery_dates'
  | 'inventory_date'
  | 'release_date'
  | 'origin'
>;

type ShufflingOrderFields = SharedOrderFields & {
  is_shuffling: true;
};

type NotShufflingOrderFields = SharedOrderFields & {
  is_shuffling: false;
};

type OrderFields = NotShufflingOrderFields | ShufflingOrderFields;

export enum CustomizationStates {
  Ready = 'Ready',
  Error = 'Error',
  Bootstrapping = 'Bootstrapping',
  Shuffling = 'Shuffling',
  Paused = 'Paused',
  Skipped = 'Skipped',
  Canceled = 'Canceled',
}

export interface CustomizationStateReady extends UseCartResult {
  state: CustomizationStates.Ready;
  order: NotShufflingOrderFields;
  accountStatus: AccountStatus;
  mutations: UseCartMutations;
}

interface CustomizationStateError {
  state: CustomizationStates.Error;
}

export interface CustomizationStateBootstrapping {
  state: CustomizationStates.Bootstrapping;
}

export interface CustomizationStateSkipped extends UseCartResult {
  state: CustomizationStates.Skipped;
  order: NotShufflingOrderFields;
  accountStatus: AccountStatus.Skipped;
  mutations: UseCartMutations;
}

export interface CustomizationStateShuffling {
  state: CustomizationStates.Shuffling;
  order: ShufflingOrderFields;
  mutations: UseCartMutations;
}

interface CustomizationStatePaused {
  state: CustomizationStates.Paused;
}

interface CustomizationStateCanceled {
  state: CustomizationStates.Canceled;
  mutations: UseCartMutations;
}

export type CustomizationState =
  | CustomizationStateReady
  | CustomizationStateError
  | CustomizationStateBootstrapping
  | CustomizationStateShuffling
  | CustomizationStatePaused
  | CustomizationStateSkipped
  | CustomizationStateCanceled;

const CustomizationContext = createContext<CustomizationState>({
  state: CustomizationStates.Bootstrapping,
});

export function useCustomization() {
  return useContext(CustomizationContext);
}

function runImmediately(action: () => void) {
  action();
}

const RunWhenCartReadyContext = createContext<RunWhenCartReady>(runImmediately);

/**
 * For cart mutations chained onto an unskip/unhold: runs `action` once the
 * order has finished any shuffle and the cart is fresh. See
 * `useDeferredCartActions` for why these can't fire straight away.
 */
export function useRunWhenCartReady() {
  return useContext(RunWhenCartReadyContext);
}

export interface Props {
  order?: OrderFields;
  accountStatus?: AccountStatus;
  isOrderDetailRequestEnabled?: boolean;
  children: ((state: CustomizationState) => ReactNode) | ReactNode;
}

function shufflingMutations(
  mutations: UseCartMutations,
  setModalContent: (content: ModalContent) => void
): UseCartMutations {
  async function showShufflingModal() {
    setModalContent({
      modalVariant: ModalVariant.Shuffling,
    });
  }

  return {
    isMutating: mutations.isMutating,
    isMutationSuccessful: mutations.isMutationSuccessful,
    addCartPairing: showShufflingModal,
    addCartProduct: showShufflingModal,
    addPairing: showShufflingModal,
    addProduct: showShufflingModal,
    addProductsToBundle: showShufflingModal,
    removePairing: showShufflingModal,
    removeProduct: showShufflingModal,
    removeProductFromBundle: showShufflingModal,
    removeRecipeFromBundle: showShufflingModal,
    swapRecipe: showShufflingModal,
  };
}

// Exported for testing
export function getCustomizationState(params: {
  currentStateVariant: CustomizationStates | null;
  order?: OrderFields;
  accountStatus?: AccountStatus;
  useCartResult: UseCartResult | BootstrappingUseCartResult;
  mutations: UseCartMutations;
  setModalContent: (content: ModalContent | null) => void;
  orderIdToDietaryRestrictionsWarningAck: Record<string, boolean>;
  onWarningAcknowledge: (orderId: number) => void;
  customerPlanServingSize?: ServingSize;
  runWhenCartReady?: RunWhenCartReady;
}): CustomizationState {
  const {
    currentStateVariant,
    order,
    useCartResult,
    accountStatus,
    mutations,
    setModalContent,
    orderIdToDietaryRestrictionsWarningAck,
    onWarningAcknowledge,
    customerPlanServingSize,
    runWhenCartReady = runImmediately,
  } = params;

  if (accountStatus === undefined) {
    return {
      state: CustomizationStates.Bootstrapping,
    };
  }

  if (accountStatus === AccountStatus.Canceled) {
    return {
      state: CustomizationStates.Canceled,
      mutations,
    };
  }

  if (accountStatus === AccountStatus.Paused) {
    return {
      state: CustomizationStates.Paused,
    };
  }

  if (order === undefined) {
    return {
      state: CustomizationStates.Bootstrapping,
    };
  }

  const { is_shuffling } = order;
  if (is_shuffling === true) {
    return {
      state: CustomizationStates.Shuffling,
      order,
      mutations: shufflingMutations(mutations, setModalContent),
    };
  }

  if (useCartResult.cart === undefined) {
    if (currentStateVariant === CustomizationStates.Shuffling) {
      // if we are in a shuffling state
      // we don't want to transition to bootstrapping
      // After we determine that an order is no longer shuffling
      // we make an HTTP request to get the cart
      // that means that there is a middle state where the order
      // is not shuffling but the cart isn't ready yet
      // We don't want the UI to switch to "Loading" state
      // So we just send a synthetic Shuffling state
      return {
        state: CustomizationStates.Shuffling,
        order: {
          ...order,
          is_shuffling: true,
        },
        mutations: shufflingMutations(mutations, setModalContent),
      };
    }

    return {
      state: CustomizationStates.Bootstrapping,
    };
  }

  if (accountStatus === AccountStatus.Skipped) {
    return {
      state: CustomizationStates.Skipped,
      ...useCartResult,
      order,
      accountStatus,
      mutations: {
        ...mutations,
        addProduct: ({
          product,
          dietaryProductIngredients = [],
          onSuccess,
          location,
          pageName,
          conversationId,
          accessedFrom,
        }) => {
          const showDietaryRestrictionsWarningModal =
            order?.id && dietaryProductIngredients.length > 0
              ? !orderIdToDietaryRestrictionsWarningAck[order.id]
              : false;

          setModalContent({
            modalVariant: showDietaryRestrictionsWarningModal
              ? ModalVariant.BypassSkipAndDietaryRestrictions
              : ModalVariant.BypassSkip,
            modalProps: {
              callout: showDietaryRestrictionsWarningModal
                ? `Note: This item doesn't match the dietary needs and preferences in your food profile.`
                : '',
              onSuccess: () => {
                onSuccess?.();
                if (showDietaryRestrictionsWarningModal) {
                  onWarningAcknowledge(order?.id);
                  analytics.track('Restriction Modal', {
                    customer_state: accountStatus,
                    add_type: 'unskip_and_update_cart',
                    product_id: product.id,
                  });
                }
                runWhenCartReady(
                  () => {
                    mutations.addProduct({
                      product,
                      location,
                      pageName,
                      conversationId,
                      accessedFrom,
                    });
                  },
                  cartActionKey('product', product.id)
                );
              },
              onCancel: () => {
                if (showDietaryRestrictionsWarningModal) {
                  analytics.track('Restriction Modal', {
                    customer_state: accountStatus,
                    cancel_type: 'cancel_button',
                    product_id: product.id,
                  });
                }
              },
              clickOffSegmentEvent: () => {
                if (showDietaryRestrictionsWarningModal) {
                  analytics.track('Restriction Modal', {
                    customer_state: accountStatus,
                    cancel_type: 'tap_outside',
                    product_id: product.id,
                  });
                }
              },
              swipeCloseSegmentEvent: () => {
                if (showDietaryRestrictionsWarningModal) {
                  analytics.track('Restriction Modal', {
                    customer_state: accountStatus,
                    cancel_type: 'swipe_down',
                    product_id: product.id,
                  });
                }
              },
            },
          });
        },
        removeProduct: ({
          product,
          onSuccess,
          location,
          pageName,
          conversationId,
          accessedFrom,
        }) => {
          setModalContent({
            modalVariant: ModalVariant.BypassSkip,
            modalProps: {
              onSuccess: () => {
                onSuccess?.();
                runWhenCartReady(
                  () => {
                    mutations.removeProduct({
                      product,
                      location,
                      pageName,
                      conversationId,
                      accessedFrom,
                    });
                  },
                  cartActionKey('product', product.id)
                );
              },
            },
          });
        },
        addPairing: ({
          pairing,
          dietaryProductIngredients = [],
          onSuccess,
          location,
          pageName,
          conversationId,
          searchId,
          resultPosition,
          accessedFrom,
        }) => {
          const showServingSizeWarningModal = getShowServingSizeWarningModal(
            pairing.servings,
            customerPlanServingSize
          );

          const showDietaryRestrictionsWarningModal =
            order?.id && dietaryProductIngredients.length > 0
              ? !orderIdToDietaryRestrictionsWarningAck[order.id]
              : false;

          const addToCartModal = {
            modalVariant: showDietaryRestrictionsWarningModal
              ? ModalVariant.BypassSkipAndDietaryRestrictions
              : ModalVariant.BypassSkip,
            modalProps: {
              callout: showDietaryRestrictionsWarningModal
                ? `Note: This recipe doesn't match the dietary needs and preferences in your food profile.`
                : '',
              onSuccess: () => {
                onSuccess?.();
                if (showDietaryRestrictionsWarningModal) {
                  onWarningAcknowledge(order?.id);
                  analytics.track('Restriction Modal', {
                    customer_state: accountStatus,
                    add_type: 'unskip_and_update_cart',
                    recipe_id: pairing.id,
                  });
                }
                runWhenCartReady(
                  () => {
                    mutations.addPairing({
                      pairing,
                      location,
                      pageName,
                      conversationId,
                      searchId,
                      resultPosition,
                      accessedFrom,
                    });
                  },
                  cartActionKey('pairing', pairing.id)
                );
              },
              onCancel: () => {
                if (showDietaryRestrictionsWarningModal) {
                  analytics.track('Restriction Modal', {
                    customer_state: accountStatus,
                    cancel_type: 'cancel_button',
                    recipe_id: pairing.id,
                  });
                }
              },
              clickOffSegmentEvent: () => {
                if (showDietaryRestrictionsWarningModal) {
                  analytics.track('Restriction Modal', {
                    customer_state: accountStatus,
                    cancel_type: 'tap_outside',
                    recipe_id: pairing.id,
                  });
                }
              },
              swipeCloseSegmentEvent: () => {
                if (showDietaryRestrictionsWarningModal) {
                  analytics.track('Restriction Modal', {
                    customer_state: accountStatus,
                    cancel_type: 'swipe_down',
                    recipe_id: pairing.id,
                  });
                }
              },
            },
          };

          if (showServingSizeWarningModal) {
            setModalContent({
              modalVariant: ModalVariant.ServingSizeWarninig,
              modalProps: {
                heading: `You added a ${pairing.servings} serving recipe`,
                onSuccess: () => {
                  setModalContent(addToCartModal);
                },
                onCancel: () => {
                  setModalContent(null);
                },
              },
            });
          } else {
            setModalContent(addToCartModal);
          }
        },
        removePairing: ({
          pairing,
          onSuccess,
          location,
          pageName,
          conversationId,
          searchId,
          resultPosition,
          accessedFrom,
        }) => {
          setModalContent({
            modalVariant: ModalVariant.BypassSkip,
            modalProps: {
              onSuccess: () => {
                onSuccess?.();
                runWhenCartReady(
                  () => {
                    mutations.removePairing({
                      pairing,
                      location,
                      pageName,
                      conversationId,
                      searchId,
                      resultPosition,
                      accessedFrom,
                    });
                  },
                  cartActionKey('pairing', pairing.id)
                );
              },
            },
          });
        },
      },
    };
  }

  return {
    state: CustomizationStates.Ready,
    ...useCartResult,
    accountStatus,
    order,
    mutations: {
      ...mutations,
      addProduct: ({
        product,
        dietaryProductIngredients = [],
        onSuccess,
        location,
        pageName,
        conversationId,
        accessedFrom,
      }) => {
        const showDietaryRestrictionsWarningModal =
          order?.id && dietaryProductIngredients.length > 0
            ? !orderIdToDietaryRestrictionsWarningAck[order.id]
            : false;

        if (!showDietaryRestrictionsWarningModal) {
          mutations.addProduct({
            product,
            location,
            pageName,
            conversationId,
            accessedFrom,
          });
          return;
        }

        const dietaryIngredientsText = getWarningTextForModal(
          dietaryProductIngredients
        );

        setModalContent({
          modalVariant: showDietaryRestrictionsWarningModal
            ? ModalVariant.BypassDietaryRestrictions
            : null,
          modalProps: {
            heading: `This item contains ${dietaryIngredientsText}`,
            body: `This doesn't match the dietary needs and preferences in your Food Profile. Are you sure you want to add this to your cart?`,
            onSuccess: () => {
              onSuccess?.();
              if (showDietaryRestrictionsWarningModal) {
                onWarningAcknowledge(order?.id);
                analytics.track('Restriction Modal', {
                  customer_state: accountStatus,
                  add_type: 'add_to_cart',
                  product_id: product.id,
                });
              }
              mutations.addProduct({
                product,
                location,
                pageName,
                conversationId,
                accessedFrom,
              });
            },
            onCancel: () => {
              if (showDietaryRestrictionsWarningModal) {
                analytics.track('Restriction Modal', {
                  customer_state: accountStatus,
                  cancel_type: 'cancel_button',
                  product_id: product.id,
                });
              }
            },
            clickOffSegmentEvent: () => {
              if (showDietaryRestrictionsWarningModal) {
                analytics.track('Restriction Modal', {
                  customer_state: accountStatus,
                  cancel_type: 'tap_outside',
                  product_id: product.id,
                });
              }
            },
            swipeCloseSegmentEvent: () => {
              if (showDietaryRestrictionsWarningModal) {
                analytics.track('Restriction Modal', {
                  customer_state: accountStatus,
                  cancel_type: 'swipe_down',
                  product_id: product.id,
                });
              }
            },
          },
        });
      },
      addPairing: ({
        pairing,
        dietaryProductIngredients = [],
        onSuccess,
        location,
        pageName,
        conversationId,
        searchId,
        resultPosition,
        accessedFrom,
      }) => {
        const showServingSizeWarningModal = getShowServingSizeWarningModal(
          pairing.servings,
          customerPlanServingSize
        );

        const showDietaryRestrictionsWarningModal =
          order?.id && dietaryProductIngredients.length > 0
            ? !orderIdToDietaryRestrictionsWarningAck[order.id]
            : false;

        if (
          !showDietaryRestrictionsWarningModal &&
          !showServingSizeWarningModal
        ) {
          mutations.addPairing({
            pairing,
            location,
            pageName,
            conversationId,
            searchId,
            resultPosition,
            accessedFrom,
          });
          return;
        }

        const dietaryIngredientsText = getWarningTextForModal(
          dietaryProductIngredients
        );

        const dietaryRestrictionsWarningModal = {
          modalVariant: showDietaryRestrictionsWarningModal
            ? ModalVariant.BypassDietaryRestrictions
            : null,
          modalProps: {
            heading: `This recipe contains ${dietaryIngredientsText}`,
            body: `You listed these as dietary needs on your food profile. Are you sure you want to add this to your cart?`,
            onSuccess: () => {
              onSuccess?.();
              if (showDietaryRestrictionsWarningModal) {
                onWarningAcknowledge(order?.id);
                analytics.track('Restriction Modal', {
                  customer_state: accountStatus,
                  add_type: 'add_to_cart',
                  recipe_id: pairing.id,
                });
              }
              mutations.addPairing({
                pairing,
                location,
                pageName,
                conversationId,
                searchId,
                resultPosition,
                accessedFrom,
              });
            },
            onCancel: () => {
              if (showDietaryRestrictionsWarningModal) {
                analytics.track('Restriction Modal', {
                  customer_state: accountStatus,
                  cancel_type: 'cancel_button',
                  recipe_id: pairing.id,
                });
              }
            },
            clickOffSegmentEvent: () => {
              if (showDietaryRestrictionsWarningModal) {
                analytics.track('Restriction Modal', {
                  customer_state: accountStatus,
                  cancel_type: 'tap_outside',
                  recipe_id: pairing.id,
                });
              }
            },
            swipeCloseSegmentEvent: () => {
              if (showDietaryRestrictionsWarningModal) {
                analytics.track('Restriction Modal', {
                  customer_state: accountStatus,
                  cancel_type: 'swipe_down',
                  recipe_id: pairing.id,
                });
              }
            },
          },
        };

        if (showServingSizeWarningModal) {
          setModalContent({
            modalVariant: ModalVariant.ServingSizeWarninig,
            modalProps: {
              heading: `You added a ${pairing.servings} serving recipe`,
              onSuccess: () => {
                if (showDietaryRestrictionsWarningModal) {
                  setModalContent(dietaryRestrictionsWarningModal);
                } else {
                  mutations.addPairing({
                    pairing,
                    location,
                    pageName,
                    conversationId,
                    searchId,
                    resultPosition,
                    accessedFrom,
                  });
                  setModalContent(null);
                }
              },
              onCancel: () => {
                setModalContent(null);
              },
            },
          });
        } else {
          setModalContent(dietaryRestrictionsWarningModal);
        }
      },
    },
  };
}

function getUseCartEnabled(props: Pick<Props, 'accountStatus' | 'order'>) {
  const { accountStatus, order } = props;
  if (
    accountStatus !== AccountStatus.Active &&
    accountStatus !== AccountStatus.Skipped
  ) {
    return false;
  }

  if (order === undefined) {
    return false;
  }

  if (isEmbeddedWebview({ location: window.location })) {
    return false;
  }

  const { is_shuffling } = order;
  return !is_shuffling;
}

export enum ModalVariant {
  BypassSkip = 'BypassSkip',
  BypassDietaryRestrictions = 'BypassDietaryRestrictions',
  BypassSkipAndDietaryRestrictions = 'BypassSkipAndDietaryRestrictions',
  Shuffling = 'Shuffling',
  ServingSizeWarninig = 'ServingSizeWarning',
}

interface ModalProps {
  callout?: string;
  heading?: string;
  body?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  clickOffSegmentEvent?: () => void;
  swipeCloseSegmentEvent?: () => void;
}
// the reasoning for this ModalContent interface is because we have various modals which all consume
// a different set of APIs; once we consolidate those, the intent is to make them all flexible, and
// accept `modalProps`, but for now, we'll need to use `modalProps` and `modalVariant` to determine
// what type of modal we're using
interface ModalContent {
  modalVariant: ModalVariant | null;
  modalProps?: ModalProps;
}

export function CustomizationProvider(props: Props) {
  const {
    children,
    order,
    accountStatus,
    isOrderDetailRequestEnabled = true,
  } = props;
  const [stateVariant, setStateVariant] = useState<CustomizationStates | null>(
    null
  );
  const [modalContent, setModalContent] = useState<ModalContent | null>(null);

  const useCartResult = useCart({
    // this param needs to be renamed
    enabled: getUseCartEnabled(props) && isOrderDetailRequestEnabled,
    accountStatus,
  });

  const useCartMutationsResult = useCartMutations();
  const { servingSize: customerPlanServingSize } = useCustomerServingSize();

  const {
    orderIdToDietaryRestrictionsWarningAck,
    setOrderIdToDietaryRestrictionsWarningAck,
  } = useDietaryRestrictionsWarningAck();

  const onWarningAcknowledge = (orderId: number) => {
    if (orderId) {
      setOrderIdToDietaryRestrictionsWarningAck(
        (prev: Record<number, boolean> | undefined) => {
          return { ...prev, [orderId]: true };
        }
      );
    }
  };

  // Holding cart changes is only needed when an unskip can shuffle the order.
  // Until the config loads, or if it can't be read, keep the immediate path.
  const { data: shuffleOnUnskipConfig } = useDynamicConfiguration(
    DynamicConfigs.ShuffleOrderOnUnskip
  );

  // Keyed off the committed state variant: the callback has to exist before
  // this render's state is derived, and the effect that flushes it re-runs
  // once `stateVariant` catches up.
  const { runWhenCartReady, pendingKeys } = useDeferredCartActions({
    isEnabled: shuffleOnUnskipConfig?.value === '1',
    isCartReady: stateVariant === CustomizationStates.Ready,
    isAbandoned:
      stateVariant === CustomizationStates.Error ||
      stateVariant === CustomizationStates.Paused ||
      stateVariant === CustomizationStates.Canceled,
  });

  const customizationState = getCustomizationState({
    currentStateVariant: stateVariant,
    order,
    accountStatus,
    useCartResult,
    mutations: useCartMutationsResult,
    setModalContent,
    orderIdToDietaryRestrictionsWarningAck,
    onWarningAcknowledge,
    customerPlanServingSize: customerPlanServingSize,
    runWhenCartReady,
  });

  useEffect(() => {
    setStateVariant(customizationState.state);
  }, [customizationState.state]);

  const bypassSkipOrBypassSkipAndDietaryRestrictions =
    modalContent?.modalVariant === ModalVariant.BypassSkip ||
    modalContent?.modalVariant ===
      ModalVariant.BypassSkipAndDietaryRestrictions;

  return (
    <CustomizationContext.Provider value={customizationState}>
      <RunWhenCartReadyContext.Provider value={runWhenCartReady}>
        <PendingCartActionsContext.Provider value={pendingKeys}>
          {modalContent?.modalVariant === ModalVariant.Shuffling && (
            <FillingCartModal
              isOpen={modalContent?.modalVariant === ModalVariant.Shuffling}
              onClose={() => {
                setModalContent(null);
              }}
            />
          )}
          {bypassSkipOrBypassSkipAndDietaryRestrictions && (
            <AddToCartModal
              testid="add-button-add-to-cart-modal"
              initialShowModal={bypassSkipOrBypassSkipAndDietaryRestrictions}
              accountStatus={accountStatus}
              orderId={order?.id}
              callout={modalContent?.modalProps?.callout}
              onAccountChangeSuccess={() => {
                modalContent?.modalProps?.onSuccess?.();
                setModalContent(null);
              }}
              showXButton={false}
              onCancel={() => {
                modalContent?.modalProps?.onCancel?.();
                setModalContent(null);
              }}
            />
          )}
          {modalContent?.modalVariant ===
            ModalVariant.BypassDietaryRestrictions && (
            <StyledSheetOrModal
              ariaLabel="Bypass product dietary restrictions warning modal"
              testid="add-remove-button-bypass-warning-add-to-cart-modal"
              isOpen={true}
              showXButton={false}
              heading={modalContent?.modalProps?.heading || ''}
              body={modalContent?.modalProps?.body || ''}
              confirmButtonText="Add to cart"
              secondaryButtonText="Cancel"
              onActionClick={() => {
                modalContent?.modalProps?.onSuccess?.();
                setModalContent(null);
              }}
              onDismiss={() => {
                modalContent?.modalProps?.onCancel?.();
                setModalContent(null);
              }}
            />
          )}
          {modalContent?.modalVariant === ModalVariant.ServingSizeWarninig && (
            <ServingSizeWarningModal
              testid="add-remove-button-outside-plan-serving-size-modal"
              heading={modalContent?.modalProps?.heading || ''}
              onActionClick={() => modalContent?.modalProps?.onSuccess?.()}
              onDismiss={() => {
                modalContent?.modalProps?.onSuccess?.();
                setModalContent(null);
              }}
            />
          )}
          {typeof children === 'function'
            ? children(customizationState)
            : children}
        </PendingCartActionsContext.Provider>
      </RunWhenCartReadyContext.Provider>
    </CustomizationContext.Provider>
  );
}
